/**
 * Cliente de la API de Tarifas (tag `tarifas` del `openapi.yaml`) — Admin Web.
 *
 * Endpoints (US-2.4):
 * - GET    /api/v1/tariffs
 * - POST   /api/v1/tariffs
 * - GET    /api/v1/tariffs/{tariffId}
 * - PATCH  /api/v1/tariffs/{tariffId}
 * - DELETE /api/v1/tariffs/{tariffId} (soft-delete idempotente)
 *
 * Tipos derivados del contrato en `@goelectric/shared-types`; mismo patrón de red
 * que `locationApi`/`allyApi` (sobre `apiRequest`).
 */
import type {
  CreateTariffBody,
  ListTariffsQuery,
  Tariff,
  TariffsPage,
  UpdateTariffBody,
} from '@goelectric/shared-types';
import { apiRequest } from '@/lib/http/apiClient';

/** Query params del listado (filtros + offset/limit), tal como los define el contrato. */
export type TariffListParams = ListTariffsQuery;

/** Serializa los params definidos (omite `undefined`/`null`/vacío) a `?a=b&c=d`. */
function toQuery(params: TariffListParams): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params) as [string, unknown][]) {
    if (value !== undefined && value !== null && value !== '') {
      search.set(key, String(value));
    }
  }
  const query = search.toString();
  return query ? `?${query}` : '';
}

export const tariffApi = {
  list(params: TariffListParams = {}): Promise<TariffsPage> {
    return apiRequest<TariffsPage>(`/api/v1/tariffs${toQuery(params)}`, {
      retryOnUnauthorized: true,
    });
  },

  get(tariffId: string): Promise<Tariff> {
    return apiRequest<Tariff>(`/api/v1/tariffs/${encodeURIComponent(tariffId)}`);
  },

  create(body: CreateTariffBody): Promise<Tariff> {
    return apiRequest<Tariff>('/api/v1/tariffs', { method: 'POST', body });
  },

  update(tariffId: string, body: UpdateTariffBody): Promise<Tariff> {
    return apiRequest<Tariff>(`/api/v1/tariffs/${encodeURIComponent(tariffId)}`, {
      method: 'PATCH',
      body,
    });
  },

  remove(tariffId: string): Promise<void> {
    return apiRequest<void>(`/api/v1/tariffs/${encodeURIComponent(tariffId)}`, {
      method: 'DELETE',
    });
  },
};
